import React, { useCallback, useEffect, useState } from 'react';

import { buttons, colors, typography } from '@pray/shared/components/foundations';
import AppBar from '@pray/shared/components/ui/AppBar';
import Button from '@pray/shared/components/ui/Button';
import Text from '@pray/shared/components/ui/Text';
import TextInput from '@pray/shared/components/ui/TextInput';
import { EVENTS } from '@pray/shared/constants';
import useDebounce from '@pray/shared/hooks/useDebounce';
import SuccessCheckmark from '@pray/shared/icons/success-checkmark';
import { getFirstAndLastName } from '@pray/shared/utils';
import { cn } from '@pray/shared/utils/styles';
import { isValidName } from '@pray/shared/utils/validation';

import { StepContent } from './constants';
import type { OnboardingStepProps } from '../../types';

import styles from './NameOnboardingStep.module.scss';

const NAME_MAX_LENGTH = 50;

export default function NameOnboardingStep({ form, onContinue, onBack, onSuccess, ...props }: OnboardingStepProps) {
  const { title, subtitle, logo, hasLogo, hasAppBar, showBackButton, showSkipButton } = props;

  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState('');
  const debouncedName = useDebounce(form.values.name, 450);

  useEffect(() => {
    const isValidNameValue = isValidName(debouncedName);
    const { firstName, lastName } = getFirstAndLastName(debouncedName);

    if (debouncedName) {
      if (!isValidNameValue) {
        setError(StepContent.NAME.ERROR_MESSAGE);
      } else if (!firstName || !lastName) {
        setError(StepContent.NAME.FIRST_AND_LAST_NAME_ERROR_MESSAGE);
      } else {
        setError('');
      }
      setIsValid(isValidNameValue && firstName && lastName);
    } else {
      setError('');
      setIsValid(false);
    }
  }, [debouncedName]);

  const skipButton = (
    <Button onClick={() => onContinue({ isSkipped: true })} style={{ paddingRight: 0 }}>
      Skip
    </Button>
  );

  const handleContinue = () => {
    const { firstName, lastName } = form.values;
    onSuccess?.({ firstName, lastName });
    onContinue();
  };

  const handleNameChange = useCallback((event) => {
    const { value } = event.target;
    const { firstName, lastName } = getFirstAndLastName(value);

    form.setValue('name', value);
    form.setValue('firstName', firstName);
    form.setValue('lastName', lastName);
  }, []);

  const handleKeyUp = (e) => {
    if (e.key === 'Enter') {
      if (isValid) handleContinue();
    }
  };

  return (
    <div data-viewname={EVENTS.PRAY_EVENTS.VIEW_EVENT_NAMES.ONBOARDING_FLOW.NAME_STEP}>
      {hasAppBar && (
        <AppBar center={hasLogo && logo} right={showSkipButton && skipButton} onBack={showBackButton && onBack} />
      )}
      <div className={cn(styles.container, hasAppBar ? styles.hasAppBar : '')}>
        <div className={styles.body}>
          <div className={cn(styles.headline)}>
            <Text variant={typography.heading_xl} className={styles.title}>
              {title}
            </Text>

            <Text color={colors.text_secondary} className={styles.subtitle}>
              {subtitle}
            </Text>
          </div>

          <div className={styles.inputWrap}>
            <TextInput
              className="p-0"
              name="name"
              label={StepContent.NAME.LABEL}
              placeholder={StepContent.NAME.PLACEHOLDER}
              autoFocus
              maxLength={NAME_MAX_LENGTH}
              value={form.values.name}
              errorMessage={error}
              rightIcon={isValid && <SuccessCheckmark />}
              onChange={handleNameChange}
              onKeyUp={handleKeyUp}
            />
          </div>
          <div className={styles.footer}>
            <Button full variant={buttons.variant.primary} onClick={handleContinue} disabled={!isValid}>
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
